<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">LINE 訊息範本</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'LineMsgTemplateCreate' }" class="btn btn-primary">
                <i class="ri-add-line"></i> 新增範本
              </router-link>
      </div>
    </div>

    <div class="page-container">
    <!-- 訊息範本說明 -->
    <div class="line-msg-templates-intro" :class="{ 'is-expanded': introOpen }">
      <button type="button" class="intro-toggle" @click="introOpen = !introOpen">
        <span class="intro-title">
          <i class="ri-information-line"></i>
          訊息範本的使用範圍
        </span>
        <span class="intro-toggle-action">
          <span class="intro-toggle-label">{{ introOpen ? '收起' : '查看說明' }}</span>
          <i :class="introOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
        </span>
      </button>
      <div v-show="introOpen" class="intro-body">
        <p class="intro-text">在此集中管理 LINE 訊息內容（文字、圖片、Flex 等），建立一次即可在多個功能中重複選用，無需每次重新編輯。</p>
        <ul class="intro-list">
          <li><strong>使用範圍</strong>：LINE 推播、行銷活動、自動回覆、Postback 規則、加好友行銷、集點獎勵、分享獎勵、點數過期提醒等需要發送 LINE 訊息的功能。</li>
          <li><strong>個人訊息</strong>：一對一發送時使用，可插入 <code v-pre>{{會員姓名}}</code> 等變數，依每位會員自動替換內容。適用自動回覆、行銷活動推播、獎勵通知等。</li>
          <li><strong>群發訊息</strong>：多人批次發送（如 LINE 推播），內容固定，不進行會員變數替換。</li>
          <li><strong>提示</strong>：各功能選擇範本時會依用途自動篩選對應類型；僅「啟用」中的範本會出現在選單中。</li>
        </ul>
      </div>
    </div>

    <!-- 篩選工具列 -->
    <div class="page-toolbar">
      <div class="search-box">
        <input v-model="filters.keyword" type="text" class="form-control" placeholder="搜尋範本名稱..." @input="debounceSearch" />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group filter-selects">
        <select v-model="filters.messageType" class="form-control filter-select" @change="loadList">
          <option value="">全部類型</option>
          <option v-for="t in msgTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
        </select>
        <select v-model="filters.status" class="form-control filter-select" @change="loadList">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
        </select>
        <select v-model="filters.audienceType" class="form-control filter-select" @change="loadList">
          <option value="">全部用途</option>
          <option value="personal">個人訊息</option>
          <option value="broadcast">群發訊息</option>
        </select>
      </div>
    </div>

    <!-- 表格 -->
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>範本名稱</th>
            <th>訊息類型</th>
            <th>用途</th>
            <th>預設版型</th>
            <th>狀態</th>
            <th>更新時間</th>
            <th style="width:190px;">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="7" class="text-center">載入中...</td>
          </tr>
          <tr v-else-if="items.length === 0">
            <td colspan="7" class="text-center">尚無範本資料</td>
          </tr>
          <tr v-for="item in items" :key="item.id">
            <td>
              <div class="template-name">{{ item.name }}</div>
              <div v-if="item.description" class="template-desc">{{ item.description }}</div>
            </td>
            <td><span class="badge" :class="'badge-' + item.messageType">{{ typeLabel(item.messageType) }}</span></td>
            <td>
              <span class="badge badge-audience" :class="item.audienceType === 'broadcast' ? 'audience-broadcast' : 'audience-personal'">
                {{ audienceLabel(item.audienceType) }}
              </span>
            </td>
            <td>{{ item.flexPreset ? presetLabel(item.flexPreset) : '—' }}</td>
            <td>
              <span class="status-dot" :class="item.status === 'active' ? 'active' : 'inactive'"></span>
              {{ item.status === 'active' ? '啟用' : '停用' }}
            </td>
            <td>{{ formatDate(item.updatedAt) }}</td>
            <td>
              <div class="action-btns">
                <router-link :to="{ name: 'LineMsgTemplateEdit', params: { id: item.id } }" class="btn btn-sm btn-primary" title="編輯">
                  <i class="ri-edit-line"></i> 編輯
                </router-link>
                <button class="btn btn-sm btn-secondary" :disabled="duplicatingId !== null" @click="handleDuplicate(item)" title="複製範本與檔案">
                  <i class="ri-file-copy-line"></i> {{ duplicatingId === item.id ? '複製中' : '複製' }}
                </button>
                <button class="btn btn-sm btn-danger" @click="handleDelete(item)" title="刪除">
                  <i class="ri-delete-bin-line"></i> 刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分頁 -->
    <ListPagination
      v-if="pagination.total > 0"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      :total="pagination.total"
      @change="goPage"
    />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';

const loading = ref(false);
const introOpen = ref(false);
const items = ref([]);
const pagination = reactive({ page: 1, pageSize: 20, total: 0, totalPages: 1 });
const filters = reactive({ keyword: '', messageType: '', status: '', audienceType: '' });

const msgTypes = [
  { value: 'text', label: '文字' },
  { value: 'image', label: '圖片' },
  { value: 'video', label: '影片' },
  { value: 'image_carousel', label: '圖片輪播' },
  { value: 'imagemap', label: '圖文' },
  { value: 'flex', label: 'Flex' },
  { value: 'flex_carousel', label: 'Flex輪播' }
];

const presetLabels = {
  product_card: '商品卡片', coupon_card: '優惠券', event_announce: '活動公告',
  order_confirm: '訂單通知', member_welcome: '會員歡迎', image_text: '圖文卡片',
  image_banner: '圖片＋底部文字', template_card: '按鈕卡片',
  business_card: '名片',
  card_portrait_full: '名片：滿版人像', card_avatar_center: '名片：圓形人像中心',
  card_company_hero: '名片：頂部公司滿版人像', card_gradient_banner: '名片：漸層橫幅',
  card_minimal_text: '名片：極簡文字',
  custom: '自訂'
};

function typeLabel(type) {
  const t = msgTypes.find(x => x.value === type);
  return t ? t.label : type;
}

function audienceLabel(aud) {
  return aud === 'broadcast' ? '群發' : '個人';
}
function presetLabel(preset) { return presetLabels[preset] || preset; }

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('zh-TW', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}

let searchTimer = null;
function debounceSearch() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => { pagination.page = 1; loadList(); }, 300);
}

async function loadList() {
  loading.value = true;
  try {
    const params = {
      page: pagination.page,
      pageSize: pagination.pageSize,
      ...filters.keyword && { keyword: filters.keyword },
      ...filters.messageType && { messageType: filters.messageType },
      ...filters.status && { status: filters.status },
      ...filters.audienceType && { audienceType: filters.audienceType }
    };
    const res = await lineMsgTemplateService.list(params);
    if (res?.success && res.data) {
      items.value = res.data.items || [];
      const pg = res.data.pagination || {};
      pagination.total = pg.totalItems ?? pg.total ?? 0;
      pagination.totalPages = pg.totalPages || 1;
      pagination.page = pg.page || 1;
    }
  } catch (err) {
    console.error('載入失敗:', err);
  } finally {
    loading.value = false;
  }
}

function goPage(p) {
  pagination.page = p;
  loadList();
}

const duplicatingId = ref(null);
async function handleDuplicate(item) {
  if (duplicatingId.value) return;
  if (!confirm(`確定要複製範本「${item.name}」？\n內容中的圖片／影片檔案也會一併複製。`)) return;
  duplicatingId.value = item.id;
  try {
    await lineMsgTemplateService.duplicate(item.id);
    await loadList();
  } catch (err) {
    alert('複製失敗：' + (err?.response?.data?.message || err.message));
  } finally {
    duplicatingId.value = null;
  }
}

async function handleDelete(item) {
  if (!confirm(`確定要刪除範本「${item.name}」？`)) return;
  try {
    await lineMsgTemplateService.remove(item.id);
    loadList();
  } catch (err) {
    alert('刪除失敗：' + (err?.response?.data?.message || err.message));
  }
}

onMounted(() => { loadList(); });
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.line-msg-templates-intro {
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #e7f1ff;
  border: 1px solid #b6d4fe;
  border-radius: 8px;
  color: #0a58ca;
}

.line-msg-templates-intro.is-expanded {
  padding: 16px 20px;
}

.line-msg-templates-intro .intro-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.line-msg-templates-intro .intro-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.line-msg-templates-intro .intro-title i {
  font-size: 18px;
}

.line-msg-templates-intro .intro-toggle-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 13px;
  color: #084298;
}

.line-msg-templates-intro .intro-body {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(10, 88, 202, 0.15);
}

.line-msg-templates-intro .intro-text {
  margin: 0 0 10px 0;
  font-size: 14px;
  line-height: 1.5;
}

.line-msg-templates-intro .intro-list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.6;
}

.line-msg-templates-intro .intro-list li {
  margin-bottom: 4px;
}

.line-msg-templates-intro .intro-list code {
  padding: 1px 4px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 3px;
  font-size: 12px;
}

.filter-select {
  width: auto; min-width: 120px; padding: 6px 10px; font-size: 13px;
}

.filter-selects {
  display: flex;
  gap: 6px;
}

.template-name { font-weight: 500; color: #374151; }
.template-desc { font-size: 12px; color: #9ca3af; margin-top: 2px; }

.badge {
  display: inline-block; padding: 2px 8px; border-radius: 4px;
  font-size: 11px; font-weight: 500;
}
.badge-text { background: #E0F2FE; color: #0369A1; }
.badge-image { background: #FCE7F3; color: #BE185D; }
.badge-video { background: #FEF3C7; color: #92400E; }
.badge-image_carousel { background: #F3E8FF; color: #7C3AED; }
.badge-imagemap { background: #ECFDF5; color: #065F46; }
.badge-flex { background: #EFF6FF; color: #1D4ED8; }
.badge-flex_carousel { background: #FFF7ED; color: #C2410C; }

.badge-audience.audience-personal { background: #ecfdf5; color: #047857; }
.badge-audience.audience-broadcast { background: #fef3c7; color: #b45309; }

.status-dot {
  display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px;
}
.status-dot.active { background: #10B981; }
.status-dot.inactive { background: #D1D5DB; }

.action-btns { display: flex; gap: 6px; }
.action-btns .btn { white-space: nowrap; }

.table-wrapper table { min-width: 780px; }
</style>
